import axios from "axios";
import { useState, useEffect } from "react";
import {
  CheckCircle,
  AlertCircle,
  X,
  Calendar,
  Plus,
  ChevronRight,
  Search,
  Layers,
  Check,
  MapPin,
  UtensilsCrossed,
  Info,
  Edit3,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "../guardarToken/themeContext";
import BotonAcceso from "../elementos/BotonAcceso";

const apiUrlPlatos = process.env.REACT_APP_API_PLATOS || "/api/menus/platos/";
const apiUrlMenuSemanals =
  process.env.REACT_APP_API_MENUSEMANALS || "/api/menus/menu-semanals/";

// Determinar el origen base para construir URLs absolutas si es necesario
const apiOrigin = apiUrlPlatos.startsWith("http")
  ? new URL(apiUrlPlatos).origin
  : window.location.origin;

// Rutas base limpias
const apiBaseMenu = apiUrlPlatos.startsWith("http")
  ? apiUrlPlatos.replace(/\/platos\/?$/, "")
  : `${apiOrigin}${apiUrlPlatos.replace(/\/platos\/?$/, "")}`;

const apiBaseSedes = `${apiOrigin}/api/sedes`;
const apiBaseTipos = `${apiBaseMenu}/tipos-menu`;
const apiPlatosFinal = apiUrlPlatos.startsWith("http")
  ? apiUrlPlatos
  : `${apiOrigin}${apiUrlPlatos}`;
const apiMenuSemanalsFinal = apiUrlMenuSemanals.startsWith("http")
  ? apiUrlMenuSemanals
  : `${apiOrigin}${apiUrlMenuSemanals}`;

function CargaMenuSemanal() {
  const { isDarkMode } = useTheme();

  // CONFIGURACIÓN MULTI-EMPRESA
  // Colocar aquí los IDs de las sedes que son externas para cambiar el color de la interfaz
  // Ejemplo: const EXTERNAL_SEDE_IDS = [4, 5]; 
  const EXTERNAL_SEDE_IDS = [];

  const [platos, setPlatos] = useState([]);
  const [tiposMenu, setTiposMenu] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [selectedTipo, setSelectedTipo] = useState(null);
  const [searchTerms, setSearchTerms] = useState({});
  const [platosSeleccionados, setPlatosSeleccionados] = useState([]);
  const [fechaSeleccionada, setFechaSeleccionada] = useState("");
  const [sedesSeleccionadas, setSedesSeleccionadas] = useState([]); // Cambiado a array para múltiples sedes
  const [sedeSeleccionada, setSedeSeleccionada] = useState(""); // Filtro visual (para ver qué se cargó)

  // Lógica de TEMA DINÁMICO
  const isExternal = EXTERNAL_SEDE_IDS.includes(Number(sedeSeleccionada));
  const themeColor = isExternal ? "indigo" : "orange"; // Color base (Tailwind)

  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ visible: false, tipo: "", mensaje: "" });
  const [fechasCargadas, setFechasCargadas] = useState([]);
  const [currentGuideTime, setCurrentGuideTime] = useState(() => new Date());
  const [viewFechasCargadas, setViewFechasCargadas] = useState(true);
  const [tiposSede, setTiposSede] = useState([]); // Tipos habilitados para esta sede
  const [showAllTypes, setShowAllTypes] = useState(false); // Alternar entre sugeridos y todos
  const [editandoFecha, setEditandoFecha] = useState(null); // Fecha que se está editando

  const usuario = localStorage.getItem("nombreUsuario") || "anon";
  const ORDER_KEY = `cargaMenuOrder_${usuario}`;
  const [tipoOrder, setTipoOrder] = useState(() => {
    const saved = localStorage.getItem(ORDER_KEY);
    return saved ? JSON.parse(saved) : [];
  });

  // Hook para cargar sedes y tipos
  const cargarSedesYTipos = async () => {
    try {
      setLoading(true);
      const [resSedes, resTipos] = await Promise.all([
        axios.get(apiBaseSedes),
        axios.get(apiBaseTipos),
      ]);

      const activeSedes = resSedes.data.filter((s) => s.activo);
      setSedes(activeSedes);
      setTiposMenu(resTipos.data);

      if (activeSedes.length > 0) {
        setSedeSeleccionada(activeSedes[0].id);
      }

      if (resTipos.data.length > 0) {
        setSelectedTipo(resTipos.data[0]);
        if (tipoOrder.length === 0) {
          setTipoOrder(resTipos.data.map((t) => t.nombre));
        }
      }
    } catch (error) {
      console.error("Error inicializando datos:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarSedesYTipos();
    // También refrescar cuando la pestaña vuelve a estar visible
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        cargarSedesYTipos();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentGuideTime(new Date());
    }, 60000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (sedeSeleccionada) {
      getPlatos();
      getTiposSede();
      getFechasCargadas();
    }
  }, [sedeSeleccionada]);

  const getTiposSede = async () => {
    try {
      const res = await axios.get(`${apiBaseSedes}/${sedeSeleccionada}/tipos`);
      const nombresSugeridos = res.data.map((t) => t.nombre);
      setTiposSede(nombresSugeridos);

      // Si el tipo actual no está en los sugeridos y no estamos viendo todos, auto-seleccionar el primero sugerido
      if (nombresSugeridos.length > 0 && !showAllTypes) {
        const currentNombre = selectedTipo?.nombre;
        if (!nombresSugeridos.includes(currentNombre)) {
          const firstSuggested = tiposMenu.find(
            (t) => t.nombre === nombresSugeridos[0],
          );
          if (firstSuggested) setSelectedTipo(firstSuggested);
        }
      }
    } catch (error) {
      console.error("Error al obtener tipos de la sede:", error);
    }
  };

  const getPlatos = async () => {
    try {
      // Cargamos TODOS los platos para la biblioteca global
      console.log("Cargando biblioteca global de platos...");
      const res = await axios.get(apiPlatosFinal);
      setPlatos(res.data);
    } catch (error) {
      console.error("Error al obtener la biblioteca de platos:", error);
      setModal({
        visible: true,
        tipo: "error",
        mensaje: "❌ No se pudo cargar la biblioteca de platos.",
      });
    }
  };

  const getFechasCargadas = async () => {
    try {
      const res = await axios.get(
        `${apiMenuSemanalsFinal}?sede_id=${sedeSeleccionada}`,
      );
      // Agrupar por fecha y contar platos
      const grouping = res.data.reduce((acc, current) => {
        const date = current.fecha;
        if (!acc[date]) acc[date] = 0;
        acc[date]++;
        return acc;
      }, {});

      const sortedDates = Object.entries(grouping)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([fecha, count]) => ({ fecha, count }));

      console.log("DEBUG: Sede seleccionada:", sedeSeleccionada);
      console.log("DEBUG: Fechas obtenidas:", sortedDates);

      setFechasCargadas(sortedDates);
    } catch (error) {
      console.error("Error al obtener fechas cargadas:", error);
    }
  };

  const getDayName = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString("es-ES", { weekday: "long" });
  };

  const addDays = (date, days) => {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
  };

  const getStartOfWeek = (date) => {
    const normalized = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      0,
      0,
      0,
      0,
    );
    const dayIndex = (normalized.getDay() + 6) % 7;
    normalized.setDate(normalized.getDate() - dayIndex);
    return normalized;
  };

  const getGuideWeekRange = (now) => {
    const currentWeekStart = getStartOfWeek(now);
    const resetTime = addDays(currentWeekStart, 3);
    resetTime.setHours(14, 0, 0, 0);

    const nextWeekOffset = now >= resetTime ? 14 : 7;
    const nextWeekStart = addDays(currentWeekStart, nextWeekOffset);
    const nextWeekEnd = addDays(nextWeekStart, 6);

    return {
      nextWeekStart,
      nextWeekEnd,
      resetTime,
    };
  };

  const {
    nextWeekStart,
    nextWeekEnd,
  } = getGuideWeekRange(currentGuideTime);

  const isDateWithinRange = (fecha, rangeStart, rangeEnd) => {
    const fechaMenu = new Date(`${fecha}T00:00:00`);
    return fechaMenu >= rangeStart && fechaMenu <= rangeEnd;
  };

  const fechasSemanaProxima = fechasCargadas.filter(({ fecha }) =>
    isDateWithinRange(fecha, nextWeekStart, nextWeekEnd),
  );

  // La guía acompaña el ciclo operativo:
  // hasta el jueves 14hs muestra la semana próxima;
  // desde ese momento pasa a mostrar la siguiente.
  const fechasGuiaCargadas = fechasSemanaProxima;
  const guideWeekStart = nextWeekStart;
  const guideWeekEnd = nextWeekEnd;

  const guideWeekLabel = `${guideWeekStart.toLocaleDateString("es-AR")} - ${guideWeekEnd.toLocaleDateString("es-AR")}`;

  const handleSearchChange = (e, tipoNombre) => {
    setSearchTerms({ ...searchTerms, [tipoNombre]: e.target.value });
  };

  const handleSeleccionarPlato = (plato) => {
    // Verificar si el plato ya está seleccionado
    if (platosSeleccionados.find((p) => p.id_plato === plato.id_plato)) return;

    // Verificar si ya hay un plato del mismo tipo seleccionado
    const platoMismoTipo = platosSeleccionados.find((p) => p.tipo === plato.tipo);
    if (platoMismoTipo) {
      setModal({
        visible: true,
        tipo: "error",
        mensaje: `⚠️ Ya seleccionaste un plato de tipo "${plato.tipo}". Solo puedes elegir 1 plato por tipo.`,
      });
      return;
    }

    setPlatosSeleccionados([...platosSeleccionados, plato]);
  };

  const handleRemoverPlato = (id_plato) => {
    setPlatosSeleccionados(
      platosSeleccionados.filter((p) => p.id_plato !== id_plato),
    );
  };

  const handleVaciarPlatos = () => {
    setPlatosSeleccionados([]);
  };

  const handleGuardarPlatos = async () => {
    if (platosSeleccionados.length > 0 && fechaSeleccionada) {
      if (sedesSeleccionadas.length === 0) {
        setModal({
          visible: true,
          tipo: "error",
          mensaje: "⚠️ Selecciona al menos una sede destino.",
        });
        return;
      }

      // Si estamos editando, permitir guardar en la misma fecha
      if (!editandoFecha && fechasCargadas.some((f) => f.fecha === fechaSeleccionada)) {
        setModal({
          visible: true,
          tipo: "error",
          mensaje: `❌ La fecha ${fechaSeleccionada} ya tiene un menú. Usa el botón de editar o eliminalo antes.`,
        });
        return;
      }

      try {
        const nuevosPlatos = platosSeleccionados.map((plato) => ({
          id_plato: plato.id_plato,
          nombre: plato.nombre,
          descripcion: plato.descripcion,
          tipo: plato.tipo,
          fecha: fechaSeleccionada,
        }));

        // Determinar sede_ids según la selección
        let sedeIds;
        if (sedesSeleccionadas.length === 0 || sedesSeleccionadas.includes('all')) {
          sedeIds = 'all'; // Menú global para todas las sedes
        } else {
          sedeIds = sedesSeleccionadas; // Array de IDs específicos
        }

        // Si estamos editando, primero eliminar el menú existente de TODAS las sedes
        if (editandoFecha) {
          await axios.delete(
            `${apiMenuSemanalsFinal}?fecha=${editandoFecha}`,
          );
        }

        await axios.post(apiMenuSemanalsFinal, {
          nuevosPlatos,
          sede_ids: sedeIds,
        });

        setPlatosSeleccionados([]);
        setFechaSeleccionada("");
        setSedesSeleccionadas([]);
        setEditandoFecha(null);

        // Si se cargó para una sola sede, cambiar el filtro para verla
        if (Array.isArray(sedeIds) && sedeIds.length === 1) {
          setSedeSeleccionada(sedeIds[0]);
        }

        setTimeout(() => getFechasCargadas(), 100);

        const sedesMsg = sedeIds === 'all'
          ? 'todas las sedes'
          : `${sedesSeleccionadas.length} sede(s)`;

        setModal({
          visible: true,
          tipo: "exito",
          mensaje: editandoFecha
            ? `✅ ¡Menú actualizado! ${nuevosPlatos.length} platos para el ${fechaSeleccionada}`
            : `✅ ¡Menú guardado! ${nuevosPlatos.length} platos cargados para el ${fechaSeleccionada} en ${sedesMsg}`,
        });
      } catch (error) {
        console.error("Error al guardar:", error);
        setModal({
          visible: true,
          tipo: "error",
          mensaje: "❌ Error al guardar el menú.",
        });
      }
    } else {
      setModal({
        visible: true,
        tipo: "error",
        mensaje: "❌ Selecciona platos y una fecha.",
      });
    }
  };

  const handleEliminarFecha = async (fecha) => {
    if (
      window.confirm(
        `¿Seguro que quieres eliminar el menú del ${fecha} para esta sede?`,
      )
    ) {
      try {
        await axios.delete(
          `${apiMenuSemanalsFinal}?fecha=${fecha}&sede_id=${sedeSeleccionada}`,
        );
        getFechasCargadas();
        setModal({
          visible: true,
          tipo: "exito",
          mensaje: `✅ Menú del ${fecha} eliminado para esta sede.`,
        });
      } catch (error) {
        setModal({
          visible: true,
          tipo: "error",
          mensaje: "❌ Error al eliminar el menú.",
        });
      }
    }
  };

  const handleEditarFecha = async (fecha) => {
    try {
      // Cargar los platos de TODAS las sedes para esa fecha
      const res = await axios.get(`${apiMenuSemanalsFinal}?fecha=${fecha}`);

      if (res.data.length === 0) {
        setModal({
          visible: true,
          tipo: "error",
          mensaje: "❌ No se encontraron platos para esta fecha.",
        });
        return;
      }

      // Detectar todas las sedes que tienen este menú
      const sedesConMenu = [...new Set(res.data.map(p => p.sede_id))].filter(Boolean);

      // Obtener platos únicos (sin duplicados por id_plato)
      const platosUnicos = [];
      const idsVistos = new Set();

      for (const pm of res.data) {
        if (!idsVistos.has(pm.id_plato)) {
          idsVistos.add(pm.id_plato);
          const platoBase = platos.find(p => p.id_plato === pm.id_plato);
          platosUnicos.push(platoBase || pm);
        }
      }

      setEditandoFecha(fecha);
      setFechaSeleccionada(fecha);
      setPlatosSeleccionados(platosUnicos);
      setSedesSeleccionadas(sedesConMenu.map(String));

      setModal({
        visible: true,
        tipo: "exito",
        mensaje: `📝 Editando menú del ${fecha}. ${platosUnicos.length} platos en ${sedesConMenu.length} sede(s).`,
      });
    } catch (error) {
      console.error("Error al cargar platos para editar:", error);
      setModal({
        visible: true,
        tipo: "error",
        mensaje: "❌ Error al cargar el menú para editar.",
      });
    }
  };

  const filteredPlatos = platos.filter((p) => {
    // Excluir platos que ya están seleccionados
    if (platosSeleccionados.some(ps => ps.id_plato === p.id_plato)) {
      return false;
    }

    if (!selectedTipo) return true;

    // Normalizar para comparación flexible
    const platoTipo = (p.tipo || "").toUpperCase().trim();
    const selectedNombre = (selectedTipo.nombre || "").toUpperCase().trim();

    // Si el tipo coincide exactamente o si el plato contiene el nombre del tipo (ej: "DIETAS OFICINA" coincide con "DIETAS OFICINA (INDIV)")
    const matchesTipo =
      platoTipo === selectedNombre ||
      platoTipo.includes(selectedNombre) ||
      selectedNombre.includes(platoTipo);

    const search = (searchTerms[selectedTipo.nombre] || "").toLowerCase();
    const matchesSearch = p.nombre.toLowerCase().includes(search);

    return matchesTipo && matchesSearch;
  });

  // Log para depuración avanzado
  console.log("Estado de Filtros:", {
    tipoCriterio: selectedTipo?.nombre,
    totalEnBiblioteca: platos.length,
    mostrando: filteredPlatos.length,
    ejemploTiposEnDatos: [...new Set(platos.map((p) => p.tipo))].slice(0, 5),
  });

  if (loading) {
    return (
      <div
        className={`flex justify-center items-center h-screen ${isDarkMode ? "bg-slate-950" : "bg-[#f8f5e9]"}`}
      >
        <CircularProgress color="warning" />
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen p-3 lg:p-4 font-sans transition-colors duration-300 ${isDarkMode ? "bg-slate-950 text-white" : "bg-[#f8f5e9] text-gray-800"}`}
    >
      {/* HEADER PREMIUM */}
      <div
        className={`${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-gray-100"} rounded-2xl shadow-sm p-5 mb-6 flex flex-col md:flex-row items-center justify-between border ring-1 ring-black/5 animate-in slide-in-from-top duration-700`}
      >
        <div className="flex items-center gap-4">
          <div className={`bg-gradient-to-br from-${themeColor}-500 to-${themeColor}-700 p-3 rounded-xl shadow-lg shadow-${themeColor}-950/20 transition-colors duration-500`}>
            <UtensilsCrossed className="text-white w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-black tracking-tight leading-none mb-1">
              {isExternal ? "Menú Externo" : "Cargar Menú Semanal"}
            </h1>
            <p
              className={`text-[11px] ${isDarkMode ? "text-slate-400" : "text-gray-500"} font-bold uppercase tracking-wider`}
            >
              {isExternal ? "Gestión de empresa externa" : "Planifica la oferta gastronómica por fecha"}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 mt-4 md:mt-0">
          <div className="flex flex-col items-end mr-2">
            <span className={`text-[10px] uppercase font-black text-${themeColor}-500 mb-1 transition-colors duration-500`}>
              {isExternal ? "Sede Externa" : "Sede de Trabajo"}
            </span>
            <div className="flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 text-slate-400" />
              <select
                className={`bg-transparent text-sm font-bold focus:outline-none appearance-none cursor-pointer pr-5 ${isDarkMode ? "text-white" : "text-gray-800"}`}
                onChange={(e) => setSedeSeleccionada(e.target.value)}
                value={sedeSeleccionada}
              >
                {sedes.map((s) => (
                  <option
                    key={s.id}
                    value={s.id}
                    className={isDarkMode ? "bg-slate-900" : "bg-white"}
                  >
                    {s.nombre}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-6 animate-in slide-in-from-top duration-1000 delay-100">
        <div
          className={`p-4 rounded-2xl border ${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-gray-100 shadow-sm"}`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
              <div>
                <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-500">
                  Días Cargados
                </h4>
                <p className="text-[9px] font-bold text-slate-400 mt-1">
                  Semana visible: {guideWeekLabel}
                </p>
              </div>
            </div>
            <div className="text-[9px] font-bold text-slate-400 uppercase">
              {sedes.find(s => String(s.id) === String(sedeSeleccionada))?.nombre || "Cargando..."}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
            {fechasGuiaCargadas.length > 0 ? (
              fechasGuiaCargadas.map((f) => {
                const fStr = f.fecha;
                const dateObj = new Date(fStr + 'T00:00:00'); // Forzar hora local
                const dayNameShort = dateObj.toLocaleDateString("es-ES", { weekday: "short" });
                const isEditing = editandoFecha === fStr;

                return (
                  <div
                    key={fStr}
                    className={`p-2 rounded-xl border flex flex-col items-center gap-1 transition-all animate-in zoom-in duration-300 relative group ${isEditing
                      ? "bg-orange-500/20 border-orange-500/50 ring-2 ring-orange-500"
                      : isDarkMode
                        ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-500"
                        : "bg-emerald-50 border-emerald-100 text-emerald-600"
                      }`}
                  >
                    <span className="text-[9px] font-black uppercase">
                      {dayNameShort}
                    </span>
                    <span className="text-[8px] font-bold">
                      {fStr.split("-")[2] + "/" + fStr.split("-")[1]}
                    </span>
                    <CheckCircle className="w-3 h-3 mt-1" />
                    <span className="text-[7px] font-bold opacity-70 mt-0.5">
                      {f.count} PLATOS
                    </span>

                    {/* Botón de editar */}
                    <button
                      onClick={() => handleEditarFecha(fStr)}
                      className={`absolute -top-1 -right-1 p-1.5 rounded-full transition-all shadow-lg ${isDarkMode
                          ? "bg-slate-800 hover:bg-orange-600 text-orange-400 hover:text-white border border-slate-700"
                          : "bg-white hover:bg-orange-600 text-orange-600 hover:text-white shadow-md border border-gray-200"
                        }`}
                      title="Editar menú de este día"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })
            ) : (
              <div className="col-span-full py-4 flex flex-col items-center justify-center opacity-40">
                <p className="text-[10px] uppercase font-bold text-slate-400">Sin días cargados</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* SIDEBAR CATEGORIES */}
        <div className="w-full lg:w-64 flex-shrink-0 animate-in slide-in-from-left duration-700">
          <div className="bg-orange-600 rounded-t-2xl p-3 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-white" />
              <h3 className="text-white font-black text-xs uppercase tracking-widest">
                Categorías
              </h3>
            </div>
            <button
              onClick={() => setShowAllTypes(!showAllTypes)}
              title={showAllTypes ? "Ver sugeridos" : "Ver todos"}
              className="p-1 rounded-lg bg-orange-700/50 hover:bg-orange-700 text-white transition-colors"
            >
              <Info className="w-3.5 h-3.5" />
            </button>
          </div>
          <div
            className={`flex flex-col gap-1.5 p-2 rounded-b-2xl border-x border-b ${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-gray-100 shadow-sm"}`}
          >
            {!showAllTypes &&
              tiposSede.length > 0 &&
              tiposSede.length < tiposMenu.length && (
                <div className="px-2 py-1 mb-1 bg-blue-500/10 rounded-lg flex items-center justify-between">
                  <span className="text-[9px] font-black uppercase text-blue-400">
                    Sólo Sugeridos
                  </span>
                  <button
                    onClick={() => setShowAllTypes(true)}
                    className="text-[9px] font-black text-blue-500 hover:underline"
                  >
                    Ver todos
                  </button>
                </div>
              )}
            {tiposMenu
              .filter(
                (tipo) =>
                  showAllTypes ||
                  tiposSede.length === 0 ||
                  tiposSede.includes(tipo.nombre),
              )
              .map((tipo) => (
                <button
                  key={tipo.id}
                  onClick={() => setSelectedTipo(tipo)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all border group ${selectedTipo?.id === tipo.id
                    ? "bg-orange-600 text-white border-orange-500 shadow-md transform scale-[1.02]"
                    : isDarkMode
                      ? "bg-slate-950/50 text-slate-400 border-slate-800 hover:border-orange-500/30 hover:bg-orange-500/5"
                      : "bg-gray-50/50 text-gray-600 border-gray-200 hover:border-orange-100 hover:bg-orange-50/30"
                    }`}
                >
                  <span
                    className={`text-xs font-bold ${selectedTipo?.id === tipo.id ? "text-white" : ""}`}
                  >
                    {tipo.nombre}
                  </span>
                  {selectedTipo?.id === tipo.id ? (
                    <ChevronRight className="w-4 h-4 text-orange-200" />
                  ) : (
                    <span
                      className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${isDarkMode ? "bg-slate-800 text-slate-500" : "bg-gray-100 text-gray-400"}`}
                    >
                      {
                        platos.filter((p) => {
                          const pTipo = (p.tipo || "").toUpperCase().trim();
                          const tNombre = (tipo.nombre || "")
                            .toUpperCase()
                            .trim();
                          return (
                            pTipo === tNombre ||
                            pTipo.includes(tNombre) ||
                            tNombre.includes(pTipo)
                          );
                        }).length
                      }
                    </span>
                  )}
                </button>
              ))}
          </div>
        </div>

        {/* MAIN AREA */}
        <div className="flex-1 min-w-0 space-y-6 animate-in slide-in-from-bottom duration-1000">
          {/* AVAILABLE DISHES */}
          <div
            className={`${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-gray-100"} rounded-2xl shadow-sm border overflow-hidden flex flex-col`}
          >
            <div className="p-4 bg-orange-600/5 border-b border-orange-500/10 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-black text-orange-500 uppercase tracking-tight">
                  {selectedTipo?.nombre || "Platos"}
                </h2>
                <p className="text-[10px] text-slate-500 font-bold italic">
                  Selecciona los platos para agregar al menú del día
                </p>
              </div>
              <div className="relative w-48">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Buscar..."
                  style={{ paddingLeft: "48px" }}
                  className={`w-full pr-3 py-1.5 rounded-lg text-xs font-medium focus:outline-none transition-all ${isDarkMode
                    ? "bg-slate-950 border-slate-800 text-white"
                    : "bg-gray-100 border-transparent text-gray-800"
                    }`}
                  value={searchTerms[selectedTipo?.nombre] || ""}
                  onChange={(e) => handleSearchChange(e, selectedTipo?.nombre)}
                />
              </div>
            </div>

            <div className="overflow-x-auto min-h-[300px] max-h-[400px] overflow-y-auto">
              {filteredPlatos.length > 0 ? (
                <table className="w-full text-xs">
                  <thead>
                    <tr
                      className={`${isDarkMode ? "bg-black/20 text-slate-500" : "bg-gray-50/50 text-gray-400"} text-[10px] font-black uppercase tracking-widest`}
                    >
                      <th className="px-5 py-3 text-left">Nombre</th>
                      <th className="px-5 py-3 text-left">Descripción</th>
                      <th className="px-5 py-3 text-center w-24">Acción</th>
                    </tr>
                  </thead>
                  <tbody
                    className={`divide-y ${isDarkMode ? "divide-slate-800" : "divide-gray-50"}`}
                  >
                    {filteredPlatos.map((plato) => {
                      const isAdded = platosSeleccionados.some(
                        (ps) => ps.id_plato === plato.id_plato,
                      );
                      return (
                        <tr
                          key={plato.id_plato}
                          className={`group transition-colors ${isDarkMode ? "hover:bg-slate-800/20" : "hover:bg-orange-50/10"}`}
                        >
                          <td
                            className="px-5 py-3.5 font-bold"
                            style={{
                              color: isDarkMode ? "#fb923c" : "#111827",
                            }}
                          >
                            {plato.nombre}
                          </td>
                          <td
                            className={`px-5 py-3.5 text-[11px] ${isDarkMode ? "text-slate-500" : "text-gray-500"}`}
                          >
                            {plato.descripcion || "-"}
                          </td>
                          <td className="px-5 py-3.5 text-center">
                            <button
                              disabled={isAdded}
                              onClick={() => handleSeleccionarPlato(plato)}
                              className={`p-2 rounded-lg transition-all ${isAdded
                                ? "bg-green-500/20 text-green-500 border border-green-500/20"
                                : "bg-orange-500 hover:bg-orange-600 text-white shadow-sm hover:shadow-md"
                                }`}
                            >
                              {isAdded ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <Plus className="w-4 h-4" />
                              )}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 grayscale opacity-40">
                  <UtensilsCrossed className="w-12 h-12 mb-3" />
                  <p className="text-xs font-bold uppercase tracking-widest">
                    No hay platos disponibles
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* FECHAS CARGADAS (TOGGLE) */}

        </div>

        {/* CONSTRUCTION PANEL */}
        <div className="w-full lg:w-80 flex-shrink-0 animate-in slide-in-from-right duration-1000">
          <div
            className={`${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-gray-100 shadow-xl"} rounded-2xl border p-5 sticky top-20`}
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xs font-black uppercase tracking-[0.15em] text-orange-500">
                {editandoFecha ? `Editando ${editandoFecha}` : 'Menú en Armado'}
              </h3>
              <span
                className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${isDarkMode ? "bg-slate-800 text-slate-400" : "bg-orange-50 text-orange-600"}`}
              >
                {platosSeleccionados.length} items
              </span>
            </div>

            {editandoFecha && (
              <div className="mb-4 bg-orange-500/10 border border-orange-500/30 text-orange-400 rounded-xl px-3 py-2 text-[10px] font-black uppercase tracking-wider flex items-center gap-2">
                <Edit3 className="w-4 h-4" />
                Editando menú existente
                <button
                  onClick={() => {
                    setEditandoFecha(null);
                    setPlatosSeleccionados([]);
                    setFechaSeleccionada("");
                    setSedesSeleccionadas([]);
                  }}
                  className="ml-auto text-orange-300 hover:text-white transition-colors"
                  title="Cancelar edición"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            <div
              className={`mb-6 max-h-[300px] overflow-y-auto pr-2 scroll-dark flex flex-col gap-2 min-h-[100px]`}
            >
              {platosSeleccionados.length > 0 ? (
                platosSeleccionados.map((plato) => (
                  <div
                    key={plato.id_plato}
                    className={`p-3 rounded-xl border flex items-start gap-3 transition-colors ${isDarkMode
                      ? "bg-slate-950 border-slate-800"
                      : "bg-gray-50 border-gray-100"
                      }`}
                  >
                    <div className="bg-orange-600/10 text-orange-500 p-1.5 rounded-lg">
                      <Check className="w-3 h-3" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-black truncate">
                        {plato.nombre}
                      </p>
                      <p className="text-[9px] text-slate-500 font-bold uppercase">
                        {plato.tipo}
                      </p>
                    </div>
                    <button
                      onClick={() => handleRemoverPlato(plato.id_plato)}
                      className="text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center py-10 opacity-30 text-center">
                  <Info className="w-8 h-8 mb-2" />
                  <p className="text-[10px] font-bold uppercase tracking-widest leading-none">
                    Tu lista está vacía
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-4 pt-4 border-t border-slate-700/20">
              <div>
                <label className="text-[10px] font-black text-slate-500 uppercase flex items-center gap-1.5 mb-2">
                  <Calendar className="w-3 h-3" /> Fecha del Menú
                </label>
                <input
                  type="date"
                  value={fechaSeleccionada}
                  onChange={(e) => setFechaSeleccionada(e.target.value)}
                  className={`w-full px-4 py-2.5 rounded-xl border text-sm font-bold focus:outline-none transition-all ${isDarkMode
                    ? "bg-slate-950 border-slate-800 text-white focus:border-orange-500/50 [&::-webkit-calendar-picker-indicator]:invert"
                    : "bg-gray-50 border-gray-200 text-gray-800 focus:border-orange-500"
                    }`}
                />
              </div>

              <div className="flex flex-col gap-2.5 mt-4">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[10px] font-black text-slate-500 uppercase flex items-center gap-1.5">
                    <MapPin className="w-3 h-3" /> Sedes Destino
                  </label>
                  <button
                    onClick={() => {
                      if (sedesSeleccionadas.length === sedes.length) {
                        // Si todas están seleccionadas, deseleccionar todas
                        setSedesSeleccionadas([]);
                      } else {
                        // Seleccionar todas
                        setSedesSeleccionadas(sedes.map(s => s.id));
                      }
                    }}
                    className={`text-[9px] font-black uppercase px-2 py-1 rounded-lg transition-all ${sedesSeleccionadas.length === sedes.length
                        ? "bg-orange-500 text-white"
                        : isDarkMode
                          ? "bg-slate-800 text-orange-400 hover:bg-orange-500 hover:text-white"
                          : "bg-orange-100 text-orange-600 hover:bg-orange-500 hover:text-white"
                      }`}
                  >
                    {sedesSeleccionadas.length === sedes.length ? "Deseleccionar Todas" : "Todas las Sedes"}
                  </button>
                </div>
                <div className="relative">
                  <div className="grid grid-cols-2 gap-2">
                    {sedes.map((s) => {
                      const isSelected = sedesSeleccionadas.includes(String(s.id)) || sedesSeleccionadas.includes(s.id);
                      return (
                        <button
                          key={s.id}
                          onClick={() => {
                            const id = s.id; // Mantener tipo (posiblemente numero)
                            const current = [...sedesSeleccionadas];
                            // Comparación robusta por si mezclamos strings/números
                            const index = current.findIndex(x => String(x) === String(id));

                            if (index >= 0) {
                              current.splice(index, 1);
                              setSedesSeleccionadas(current);
                            } else {
                              setSedesSeleccionadas([...current, id]);
                            }
                          }}
                          className={`p-2 rounded-xl border text-[10px] font-black uppercase tracking-wide transition-all ${isSelected
                            ? "bg-orange-500 text-white border-orange-500 shadow-md transform scale-[1.02]"
                            : isDarkMode
                              ? "bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-500"
                              : "bg-white border-gray-200 text-gray-500 hover:border-orange-200 hover:bg-orange-50"
                            }`}
                        >
                          {isSelected && <span className="mr-1">✓</span>}
                          {s.nombre}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[9px] text-slate-400 mt-1.5 font-bold text-center">
                    {sedesSeleccionadas.length === 0
                      ? "Debes seleccionar al menos una sede"
                      : `Aplicará a ${sedesSeleccionadas.length} sede(s)`}
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-2.5">
                <BotonAcceso
                  variant="accent"
                  onClick={handleGuardarPlatos}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white font-black text-[11px] uppercase tracking-wider py-3 rounded-xl shadow-lg shadow-orange-950/30"
                >
                  Guardar Menú
                </BotonAcceso>
                <button
                  onClick={handleVaciarPlatos}
                  className={`w-full py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${isDarkMode
                    ? "text-slate-500 hover:text-red-500 hover:bg-red-500/5"
                    : "text-gray-400 hover:text-red-600 hover:bg-red-50"
                    }`}
                >
                  Vaciar Lista
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MODAL / DIALOG PARA FEEDBACK */}
      <Dialog
        open={modal.visible}
        onClose={() => setModal({ ...modal, visible: false })}
        PaperProps={{
          sx: {
            borderRadius: "24px",
            bgcolor: isDarkMode ? "#0f172a" : "white",
            backgroundImage: "none",
            p: 1,
          },
        }}
      >
        <DialogContent className="flex flex-col items-center py-8 px-10">
          <div
            className={`p-4 rounded-3xl mb-4 ${modal.tipo === "exito" ? "bg-green-500/10 text-green-500" : "bg-red-500/10 text-red-500"}`}
          >
            {modal.tipo === "exito" ? (
              <CheckCircle className="w-12 h-12" />
            ) : (
              <AlertCircle className="w-12 h-12" />
            )}
          </div>
          <h3
            className={`text-lg font-black mb-2 ${isDarkMode ? "text-white" : "text-slate-800"}`}
          >
            {modal.tipo === "exito" ? "¡Éxito!" : "Ooops..."}
          </h3>
          <p
            className={`text-sm text-center font-medium ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}
          >
            {modal.mensaje}
          </p>
        </DialogContent>
        <DialogActions className="pb-6 px-10">
          <button
            onClick={() => setModal({ ...modal, visible: false })}
            className={`w-full py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all ${modal.tipo === "exito"
              ? "bg-green-500 text-white hover:bg-green-600 shadow-lg shadow-green-950/20"
              : "bg-red-500 text-white hover:bg-red-600 shadow-lg shadow-red-950/20"
              }`}
          >
            Cerrar
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default CargaMenuSemanal;
